import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Trash2, Download } from "lucide-react";
import { api } from "../api";
import { DataTable } from "../components/DataTable";
import type { Product } from "../types";

const STATUS_BADGE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700",
  rd: "bg-amber-50 text-amber-700",
  discontinued: "bg-slate-100 text-slate-500",
};

export function ProductList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["products", search],
    queryFn: () => api.listProducts({ search: search || undefined, per_page: 200 }),
  });

  const createMutation = useMutation({
    mutationFn: (d: Partial<Product>) => api.createProduct(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteProduct(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products"] }),
  });

  const columns = [
    { key: "sku", label: "SKU", sortable: true, filterable: true, width: "7rem", render: (p: Product) => (
      <span className="font-mono text-xs">{p.sku ?? "-"}</span>
    )},
    { key: "name", label: "產品名稱", sortable: true, filterable: true, render: (p: Product) => (
      <span className="font-medium text-slate-900">{p.name}</span>
    )},
    { key: "category", label: "類別", sortable: true, filterable: true },
    { key: "unit_price", label: "售價", sortable: true, align: "right" as const, render: (p: Product) => (
      <span className="font-mono">{p.unit_price != null ? `$${p.unit_price}` : "-"}</span>
    )},
    { key: "unit_cost", label: "成本", sortable: true, align: "right" as const, render: (p: Product) => (
      <span className="font-mono">{p.unit_cost != null ? `$${p.unit_cost}` : "-"}</span>
    )},
    { key: "status", label: "狀態", sortable: true, filterable: true, width: "6rem", render: (p: Product) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[p.status] ?? ""}`}>
        {p.status}
      </span>
    )},
  ];

  return (
    <div className="max-w-5xl space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">產品</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={16} /> 新增
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="搜尋產品名稱或 SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Quick create form */}
      {showForm && (
        <form
          className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            createMutation.mutate({
              name: fd.get("name") as string,
              sku: (fd.get("sku") as string) || undefined,
              category: (fd.get("category") as string) || undefined,
              unit_price: fd.get("unit_price") ? Number(fd.get("unit_price")) : undefined,
              unit_cost: fd.get("unit_cost") ? Number(fd.get("unit_cost")) : undefined,
            });
          }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <input name="name" className="input" placeholder="產品名稱 *" required />
            <input name="sku" className="input" placeholder="SKU" />
            <input name="category" className="input" placeholder="類別" />
            <input name="unit_price" className="input" placeholder="售價" type="number" step="0.01" />
            <input name="unit_cost" className="input" placeholder="成本" type="number" step="0.01" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              儲存
            </button>
            <button type="button" className="btn" onClick={() => setShowForm(false)}>
              取消
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        onRowClick={(p) => navigate(`/products/${p.id}`)}
        selectable
        batchActions={[
          {
            label: "批次刪除",
            icon: Trash2,
            variant: "danger",
            onClick: (items) => { items.forEach((p) => deleteMutation.mutate(p.id)); },
          },
          {
            label: "批次匯出",
            icon: Download,
            onClick: () => { /* handled by export */ },
          },
        ]}
        exportable
        exportFilename="產品列表"
        emptyMessage="沒有產品資料"
      />
    </div>
  );
}
